import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Required for fetching fonts
import Linkify from 'react-linkify';
import EmojiPicker from 'emoji-picker-react';

// UI Components
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';

// Icons
import { 
  LogOut, Star, Shield, User, Plus, Search, MessageSquare, UserX, Pin, Users, 
  Settings as SettingsIcon, X, Smile, Upload, Send, MoreVertical, MessageCircle, Phone,
  UserPlus, VolumeX, Ban, Flag 
} from 'lucide-react';

// Utils & App
import { getImageUrl } from '../../utils/imageUtils';
import { getRoleBadge } from '../../components/chat/ChatMessage';
import { API } from '../../App';
import { applyCustomFont } from '../../utils/fontUtils';
import MembersModal from '../../components/chat/modals/MembersModal';
import LudoTab from '../../components/chat/LudoTab';
import { toast } from 'sonner';
import useVoipMesh from '../../hooks/useVoipMesh';

const isImageUrl = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
};

/**
 * Desktop Layout for ChatPage
 */
const DesktopChatLayout = ({ user, setUser, state, api, pmWindows, messageHandlers, secureUrl, userRoomRole, canLeaveRoom, handleLogout, navigateToDashboard, confirmLeaveRoom }) => {
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [customFonts, setCustomFonts] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [roomCallInfo, setRoomCallInfo] = useState(null);
  const [roomInCall, setRoomInCall] = useState(false);
  const [roomMuted, setRoomMuted] = useState(false);

  const {
    rooms, selectedRoom, messages, members,
    activeTab, setActiveTab,
    searchQuery, setSearchQuery,
    pinnedMessages, showPinned, setShowPinned,
    createRoomOpen, setCreateRoomOpen,
    newRoomData, setNewRoomData,
    messageInput, setMessageInput,
    replyingTo, setReplyingTo,
    showEmojiPicker, setShowEmojiPicker,
    showGiphyPicker, setShowGiphyPicker,
    giphySearch, setGiphySearch,
    giphyResults,
    showMembersModal, setShowMembersModal,
    showMentionDropdown, mentionResults,
    mobileMenuOpen,
    ws,
    globalTheme, roomTheme,
    messagesEndRef, messageInputRef, fileInputRef,
    unreadPmCount,
    setRoomSettingsOpen,
    setLightboxMedia,
    friends, friendRequests, blockedUsers, pmRequests, staffList,
    friendsTabSection, setFriendsTabSection,
    userSearchQuery, setUserSearchQuery, userSearchResults,
    filteredRooms, pendingJoinRoom
  } = state;

  const { 
    loadRooms, selectRoom, handleJoinRoom, createRoom,
    searchUsers, sendFriendRequest, acceptFriendRequest, rejectFriendRequest,
    acceptPmRequest, rejectPmRequest, blockUser, unblockUser, unfriendUser,
    pinMessage, unpinMessage, deleteMessage, viewProfile,
    muteUser, kickUser, banUser
  } = api;

  const {
    handleMessageInputChange, selectMention, sendMessage, sendEmoji,
    onEmojiClick, searchGiphy, selectGif, handleFileUpload, handleAvatarClick
  } = messageHandlers;

  const { linkDecorator } = secureUrl;

  const canModerate = (role) => {
    if (user.role === 'super_admin' || user.role === 'staff') return true;
    return ['owner', 'admin', 'moderator'].includes(role);
  };

  const startRoomCall = async () => {
    if (!selectedRoom?.id) return;
    try {
      const res = await axios.post(`${API}/voip/start`, {
        scope: 'room',
        target_id: selectedRoom.id
      });
      const joinLink = res?.data?.join_link || '';
      setRoomCallInfo({
        id: res?.data?.call_id,
        host_id: user.id,
        participants: [user.id],
        status: 'active'
      });
      setRoomInCall(true);
      toast.success(joinLink ? `Room call started (${joinLink})` : 'Room call started');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to start room call');
    }
  };

  const endRoomCall = async () => {
    if (!roomCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${roomCallInfo.id}/end`);
      const peers = (roomCallInfo.participants || []).filter((pid) => pid && pid !== user.id);
      if (ws && ws.readyState === WebSocket.OPEN) {
        peers.forEach((peerId) => {
          ws.send(JSON.stringify({
            type: 'voip_call_end',
            call_id: roomCallInfo.id,
            scope: 'room',
            target_id: selectedRoom?.id,
            recipient_id: peerId
          }));
        });
      }
      setRoomInCall(false);
      setRoomMuted(false);
      setRoomCallInfo(null);
      toast.success('Group call ended');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to end room call');
    }
  };

  const loadActiveRoomCall = async () => {
    if (!selectedRoom?.id || user?.role === 'guest') {
      setRoomCallInfo(null);
      setRoomInCall(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/voip/active`, {
        params: { scope: 'room', target_id: selectedRoom.id }
      });
      if (response.data?.active && response.data?.call) {
        setRoomCallInfo(response.data.call);
        setRoomInCall(Boolean(response.data.is_participant));
      } else {
        setRoomCallInfo(null);
        setRoomInCall(false);
      }
    } catch {
      setRoomCallInfo(null);
      setRoomInCall(false);
    }
  };

  const joinRoomCall = async () => {
    if (!roomCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${roomCallInfo.id}/join`);
      setRoomInCall(true);
      toast.success('Joined room call');
      loadActiveRoomCall();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to join room call');
    }
  };

  const leaveRoomCall = async () => {
    if (!roomCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${roomCallInfo.id}/leave`);
      setRoomInCall(false);
      setRoomMuted(false);
      toast.success('Left room call');
      loadActiveRoomCall();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to leave room call');
    }
  };

  const isDesktopViewport = typeof window !== 'undefined' ? window.innerWidth >= 768 : true;
  const { remoteStreams: roomRemoteStreams, setMuted: setRoomVoipMuted } = useVoipMesh({
    ws,
    user,
    enabled: Boolean(isDesktopViewport && roomInCall && roomCallInfo?.id),
    callId: roomCallInfo?.id,
    scope: 'room',
    targetId: selectedRoom?.id,
    peerIds: roomCallInfo?.participants || []
  });

  useEffect(() => {
    setRoomVoipMuted(roomMuted);
  }, [roomMuted, setRoomVoipMuted]);

  useEffect(() => {
    if (!ws) return;
    const onMessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'voip_call_end' && data.call_id && roomCallInfo?.id === data.call_id) {
        setRoomInCall(false);
        setRoomMuted(false);
        setRoomCallInfo(null);
      }
    };
    ws.addEventListener('message', onMessage);
    return () => ws.removeEventListener('message', onMessage);
  }, [ws, roomCallInfo?.id]);

  // Load custom fonts
  useEffect(() => {
      const fetchCustomFonts = async () => {
          try {
              // Use the public endpoint
              const response = await axios.get(`${API}/public/fonts`);
              if (response.status === 200) { // Check status for axios
                  const data = response.data; // Axios returns data directly
                  setCustomFonts(data);
              }
          } catch (error) {
              console.error('Failed to load fonts:', error);
          }
      };
      fetchCustomFonts();
  }, []);

  // Apply font when room changes
  useEffect(() => {
      if (selectedRoom && roomTheme?.font_id && customFonts.length > 0) {
          const selectedFont = customFonts.find(f => f.id === roomTheme.font_id || f.name === roomTheme.font_family);
          if (selectedFont) {
              applyCustomFont(selectedFont.name || selectedFont.font_family, selectedFont.url, selectedFont.format);
          }
      }
  }, [selectedRoom, roomTheme, customFonts]);

  // Report user function
  const handleReportUser = async () => {
    if (!reportTarget || !reportReason.trim()) return;
    
    setReportSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reported_user_id: reportTarget.id,
          room_id: selectedRoom?.id || null,
          reason: reportReason
        })
      });
      
      if (response.ok) {
        import('sonner').then(({ toast }) => {
          toast.success('Report submitted successfully');
        });
        setReportModalOpen(false);
        setReportTarget(null);
        setReportReason('');
      } else {
        const data = await response.json();
        import('sonner').then(({ toast }) => {
          toast.error(data.detail || 'Failed to submit report');
        });
      }
    } catch (error) {
      console.error('Report error:', error);
      import('sonner').then(({ toast }) => {
        toast.error('Failed to submit report');
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  // Open report modal for a user
  const openReportModal = (memberId, memberUsername) => {
    setReportTarget({ id: memberId, username: memberUsername });
    setReportModalOpen(true);
  };

  // Load trending GIFs when picker opens
  useEffect(() => {
    if (showGiphyPicker && giphyResults.length === 0) {
      searchGiphy(''); // Empty query triggers trending
    }
  }, [showGiphyPicker]);

  useEffect(() => {
    loadActiveRoomCall();
    const id = setInterval(loadActiveRoomCall, 8000);
    return () => clearInterval(id);
  }, [selectedRoom?.id, user?.role]);

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden md:relative md:flex md:w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col overflow-y-auto">
        {/* Logo Header */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center">
          <img src="/logo.png" alt="ChatHub India" className="h-8 w-auto" onError={(e) => e.target.style.display = 'none'} />
          <span className="ml-2 text-xl font-bold text-gray-900 dark:text-gray-100">ChatHub</span>
        </div>
        
        {/* User Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarFallback className="bg-white text-blue-600 font-semibold">
                  {user.username.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center">
                  <span className="font-semibold">{user.display_name || user.username}</span>
                  {getRoleBadge(user.role)}
                </div>
                <span className="text-xs text-blue-100">@{user.username}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/20" title="Logout">
                <LogOut className="w-4 h-4" />
              </Button>
              {(user.role === 'staff' || user.role === 'super_admin' || user.role === 'premium') && (
                <Button variant="ghost" size="icon" onClick={navigateToDashboard} className="text-white hover:bg-white/20">
                  {user.role === 'premium' ? <Star className="w-4 h-4 text-amber-300" /> : <Shield className="w-4 h-4" />}
                </Button>
              )}
              {user.role !== 'guest' && (
                <Button variant="ghost" size="icon" onClick={() => window.location.href = '/profile'} className="text-white hover:bg-white/20" title="Profile">
                  <User className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="grid w-full grid-cols-3 m-2">
            <TabsTrigger value="rooms">Rooms</TabsTrigger>
            <TabsTrigger value="friends" className="relative">
              Friends
              {unreadPmCount > 0 && <Badge className="ml-2 bg-red-500 text-white px-2 py-0.5 text-xs">{unreadPmCount}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="ludo">Ludo</TabsTrigger>
          </TabsList>

          {/* Rooms Tab */}
          <TabsContent value="rooms" className="flex-1 flex flex-col mt-0">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search rooms..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 dark:bg-gray-700"
                />
              </div>
              {user.role !== 'guest' && (
                <Dialog open={createRoomOpen} onOpenChange={setCreateRoomOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full mt-3 bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" /> Create Room
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Room</DialogTitle>
                      <DialogDescription>Create a new public or private room</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Room Name</Label>
                        <Input value={newRoomData.name} onChange={(e) => setNewRoomData({ ...newRoomData, name: e.target.value })} />
                      </div>
                      <div>
                        <Label>Type</Label>
                        <select value={newRoomData.type} onChange={(e) => setNewRoomData({ ...newRoomData, type: e.target.value })} className="w-full p-2 border rounded-md">
                          <option value="public">Public</option>
                          <option value="private">Private</option>
                        </select>
                      </div>
                      <Button onClick={createRoom} className="w-full">Create</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <ScrollArea className="flex-1">
              {filteredRooms.map((room) => {
                const isMember = room.is_member || room.owner_id === user.id;
                const isOwner = room.owner_id === user.id;
                const isGlobalChat = room.name?.toLowerCase() === 'global chat' || room.name?.toLowerCase() === 'public chat';
                return (
                  <div
                    key={room.id}
                    className={`p-3 border-b hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      selectedRoom?.id === room.id ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => selectRoom(room)}>
                        <div className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                          <span className="truncate">{room.name}</span>
                          {isOwner && <Badge variant="secondary" className="text-[10px]">Owner</Badge>}
                          {isMember && !isOwner && <Badge className="bg-green-600 text-[10px]">Joined</Badge>}
                          {!isMember && <Badge variant="outline" className="text-[10px]">Left</Badge>}
                        </div>
                        <div className="text-xs text-gray-500">{room.type} • {room.member_count || 0} members</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!isMember ? (
                          <Button size="sm" variant="outline" onClick={() => handleJoinRoom(room)}>
                            Join
                          </Button>
                        ) : !isOwner && !isGlobalChat ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600"
                            onClick={() => {
                              if (window.confirm(`Leave ${room.name}?`)) {
                                api.leaveRoom(room.id);
                              }
                            }}
                          >
                            Leave
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
          </TabsContent>

          {/* Friends Tab */}
          <TabsContent value="friends" className="flex-1 flex flex-col mt-0 overflow-hidden">
            <div className="p-3 border-b">
              <div className="flex gap-2">
                <Input
                  placeholder="Search users..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers(userSearchQuery)}
                  className="flex-1"
                />
                <Button onClick={() => searchUsers(userSearchQuery)} size="sm">Search</Button>
              </div>
            </div>
            <ScrollArea className="flex-1">
              {userSearchResults.length > 0 && (
                <div className="p-2 bg-blue-50">
                  <p className="text-xs font-medium mb-2">Search Results</p>
                  {userSearchResults.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded mb-1">
                      <span>{u.username}</span>
                      <Button size="sm" onClick={() => sendFriendRequest(u.id)}>Add Friend</Button>
                    </div>
                  ))}
                </div>
              )}
              {friends.map((friend) => (
                <div key={friend.id} className="p-3 border-b flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={getImageUrl(friend.avatar_url)} />
                        <AvatarFallback>{friend.username?.substring(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${
                        friend.is_online || friend.online ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                    </div>
                    <div>
                      <span className="font-medium dark:text-white">{friend.display_name || friend.username}</span>
                      <p className="text-xs text-gray-500">{friend.is_online || friend.online ? 'Online' : 'Offline'}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {/* View Profile Icon */}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => viewProfile(friend.id)}
                      className="h-7 w-7 p-0"
                      title="View Profile"
                    >
                      <User className="w-4 h-4 text-blue-500" />
                    </Button>
                    {/* PM Icon */}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => pmWindows.openPMWindow(friend)}
                      className="h-7 w-7 p-0"
                      title="Send Message"
                    >
                      <MessageSquare className="w-4 h-4 text-green-500" />
                    </Button>
                    {/* Unfriend Icon */}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => unfriendUser(friend.id)}
                      className="h-7 w-7 p-0"
                      title="Unfriend"
                    >
                      <UserX className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </TabsContent>

          {/* Ludo Tab */}
          <TabsContent value="ludo" className="flex-1 mt-0 overflow-hidden">
            <LudoTab user={user} ws={ws} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Desktop Main Chat Area */}
      <div className={`hidden md:flex flex-1 flex-col ${mobileMenuOpen ? 'hidden' : ''}`}>
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b bg-white dark:bg-gray-800 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{selectedRoom.name}</h2>
                <p className="text-sm text-gray-500">
                  <span className="text-green-600 font-medium">{members.filter(m => m.online).length} online</span>
                  <span className="mx-1">â€¢</span>
                  {members.length} members
                </p>
              </div>
              <div className="flex gap-2">
                {pinnedMessages.length > 0 && (
                  <Button variant={showPinned ? "default" : "outline"} size="sm" onClick={() => setShowPinned(!showPinned)}>
                    <Pin className="w-4 h-4 mr-2" /> {pinnedMessages.length} Pinned
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setShowMembersModal(true)}>
                  <Users className="w-4 h-4 mr-2" /> 
                  <span className="text-green-600">{members.filter(m => m.online).length}</span>
                  <span className="mx-1">/</span>
                  {members.length}
                </Button>
                {canModerate(userRoomRole) && (
                  <Button variant="outline" size="sm" onClick={startRoomCall} title="Start group call">
                    <Phone className="w-4 h-4" />
                  </Button>
                )}
                {canLeaveRoom && (
                  <Button variant="outline" size="sm" onClick={() => { if (window.confirm(`Leave ${selectedRoom?.name}?`)) { confirmLeaveRoom(); } }} className="text-orange-600">
                    <LogOut className="w-4 h-4" />
                  </Button>
                )}
                {canModerate(userRoomRole) && (
                  <Button variant="outline" size="sm" onClick={() => setRoomSettingsOpen(true)}>
                    <SettingsIcon className="w-4 h-4 mr-2" /> Settings
                  </Button>
                )}
              </div>
            </div>

            {roomCallInfo && (
              <div className="px-4 py-2 border-b bg-blue-50 dark:bg-gray-700 dark:border-gray-600 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${roomInCall ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-300'}`}>
                    {roomInCall ? 'In group call' : 'Group call active'}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {(roomCallInfo.participants || []).length} participant{(roomCallInfo.participants || []).length === 1 ? '' : 's'}
                  </span>
                  <span className={`font-medium ${roomMuted ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-300'}`}>
                    {roomMuted ? 'Muted' : 'Unmuted'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {Object.entries(roomRemoteStreams).map(([peerId, stream]) => (
                    <audio
                      key={peerId}
                      autoPlay
                      playsInline
                      ref={(el) => {
                        if (!el) return;
                        if (el.srcObject !== stream) {
                          el.srcObject = stream;
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  ))}
                  {roomInCall && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setRoomMuted((prev) => !prev)}
                    >
                      {roomMuted ? 'Unmute' : 'Mute'}
                    </Button>
                  )}
                  {roomInCall && roomCallInfo?.host_id === user.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-red-600"
                      onClick={endRoomCall}
                    >
                      End
                    </Button>
                  )}
                  <Button
                    variant={roomInCall ? 'outline' : 'default'}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={roomInCall ? leaveRoomCall : joinRoomCall}
                  >
                    {roomInCall ? 'Leave' : 'Join'}
                  </Button>
                </div>
              </div>
            )}

            {/* Pinned Messages */}
            {showPinned && pinnedMessages.length > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 p-3 max-h-48 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-1">
                    <Pin className="w-4 h-4" /> Pinned Messages ({pinnedMessages.length})
                  </span>
                </div>
                {pinnedMessages.map((msg) => (
                  <div key={msg.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm mb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {msg.sender?.username} â€¢ {new Date(msg.created_at).toLocaleString()}
                        </p>
                        <div className="text-sm dark:text-gray-200 whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>
                          {isImageUrl(msg.content) ? (
                              <>
                                  <img
                                      src={getImageUrl(msg.content)}
                                      alt="Pinned image"
                                      style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', marginTop: '5px' }}
                                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                  />
                                  <span style={{ display: 'none' }}>{msg.content}</span>
                              </>
                          ) : (
                              <span>{msg.content}</span>
                          )}
                        </div>
                      </div>
                      {canModerate(userRoomRole) && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => unpinMessage(msg.id)} 
                          className="flex-shrink-0 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 p-1 h-auto"
                          title="Unpin message"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Messages */}
            <ScrollArea 
              className="flex-1 p-4 dark:bg-gray-900" 
              style={{
                backgroundImage: selectedRoom?.background_url 
                  ? `url(${selectedRoom.background_url.startsWith('http') ? selectedRoom.background_url : API.replace('/api', '') + selectedRoom.background_url})` 
                  : 'linear-gradient(to bottom right, rgb(248 250 252), rgb(239 246 255))',
                backgroundSize: 'cover'
              }}
              onScroll={(e) => {
                if (e.target.scrollTop === 0 && state.hasMoreMessages && !state.isLoadingMore) {
                  api.fetchMoreMessages();
                }
              }}
            >
              <div className="space-y-4">
                {state.isLoadingMore && (
                  <div className="flex justify-center py-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                )}
                {messages.map((msg, idx) => (
                  <div key={msg.id || idx}>
                    {msg.type === 'system' ? (
                      <div className="text-center text-sm text-gray-500 py-2">{msg.content}</div>
                    ) : (
                      <div className={`flex gap-3 ${msg.sender_id === user.id ? 'flex-row-reverse' : ''} group`}>
                        <Avatar 
                          className={`w-8 h-8 ${msg.sender_id !== user.id ? 'cursor-pointer hover:ring-2 hover:ring-blue-400' : ''}`}
                          onClick={() => msg.sender_id !== user.id && handleAvatarClick(msg.sender?.username, msg.sender_id)}
                        >
                          <AvatarImage src={getImageUrl(msg.sender?.avatar_url)} />
                          <AvatarFallback className="bg-blue-600 text-white text-xs">
                            {msg.sender?.username?.substring(0, 2).toUpperCase() || '??'}
                          </AvatarFallback>
                        </Avatar>
                        <div className={`flex flex-col ${msg.sender_id === user.id ? 'items-end' : ''}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">{msg.sender?.display_name || msg.sender?.username}</span>
                            {getRoleBadge(msg.sender?.role)}
                            <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleTimeString()}</span>
                          </div>
                          {msg.parent_message && (
                            <div className="text-xs mb-1 p-2 rounded border-l-2 border-gray-300 bg-gray-50">
                              <div className="font-semibold">Reply to {msg.parent_message.sender?.username}:</div>
                              <div className="truncate opacity-75">{msg.parent_message.content.substring(0, 50)}</div>
                            </div>
                          )}
                          <div
                            className={`px-4 py-2 rounded-2xl max-w-md ${msg.sender_id === user.id ? '' : 'border border-gray-200'}`}
                            style={{
                              backgroundColor: msg.sender_id === user.id ? (globalTheme.chat_bubble_own || '#2563EB') : (globalTheme.chat_bubble_other || '#FFFFFF'),
                              color: msg.sender_id === user.id ? '#ffffff' : (globalTheme.font_color || '#000000'),
                              fontFamily: roomTheme.font_family || 'inherit', // Apply custom font
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word'
                            }}
                          >
                            {msg.content.startsWith('http') && msg.content.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                              <img src={getImageUrl(msg.content)} alt="media" className="rounded max-w-[180px] cursor-pointer" onClick={() => setLightboxMedia({ url: getImageUrl(msg.content), type: 'image' })} />
                            ) : msg.content.startsWith('http') && msg.content.match(/\.(mp4|webm)$/i) ? (
                              <video src={getImageUrl(msg.content)} className="rounded max-w-[180px]" controls />
                            ) : (
                              <Linkify componentDecorator={linkDecorator}>
                                <span className="whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>{msg.content}</span>
                              </Linkify>
                            )}
                          </div>
                          <div className="flex gap-2 mt-1 opacity-0 group-hover:opacity-100">
                            <button onClick={() => setReplyingTo(msg)} className="text-xs text-blue-600 hover:underline">Reply</button>
                            {canModerate(userRoomRole) && !msg.pinned && (
                              <button onClick={() => pinMessage(msg.id)} className="text-xs text-yellow-600 hover:underline">Pin</button>
                            )}
                            {(msg.sender_id === user.id || canModerate(userRoomRole)) && (
                              <button onClick={() => deleteMessage(msg.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Reply indicator */}
            {replyingTo && (
              <div className="bg-blue-50 border-t px-4 py-2 flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-700 font-medium">Replying to {replyingTo.sender?.username}</p>
                  <p className="text-xs text-gray-600 truncate">{replyingTo.content.substring(0, 50)}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setReplyingTo(null)}><X className="w-4 h-4" /></Button>
              </div>
            )}

            {/* Message Input */}
            <div className="p-4 border-t bg-white dark:bg-gray-800">
              {showMentionDropdown && mentionResults.length > 0 && (
                <div className="absolute bottom-20 left-4 bg-white border rounded-lg shadow-lg p-2 z-50">
                  {mentionResults.map((m) => (
                    <div key={m.id || m.user_id} onClick={() => selectMention(m)} className="p-2 hover:bg-gray-100 cursor-pointer rounded">
                      @{m.username}
                    </div>
                  ))}
                </div>
              )}
              {showEmojiPicker && (
                <div className="absolute bottom-20 left-4 z-50">
                  <EmojiPicker onEmojiClick={onEmojiClick} />
                </div>
              )}
              {showGiphyPicker && (
                <div className="absolute bottom-20 left-4 z-50 bg-white border rounded-lg shadow-lg p-3 w-72">
                  <Input
                    placeholder="Search GIFs..."
                    value={giphySearch}
                    onChange={(e) => setGiphySearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchGiphy(giphySearch)}
                    className="mb-2"
                  />
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {giphyResults.map((gif, i) => (
                      <img
                        key={gif.id || i}
                        src={getImageUrl(gif.images?.fixed_height_small?.url)}
                        alt="GIF"
                        className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80"
                        onClick={() => selectGif(gif.images?.original?.url)}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                  <Smile className="w-5 h-5" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setShowGiphyPicker(!showGiphyPicker)}>
                  GIF
                </Button>
                <label className="cursor-pointer">
                  <Button variant="outline" size="icon" asChild><span><Upload className="w-5 h-5" /></span></Button>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,video/*" />
                </label>
                <Input
                  ref={messageInputRef}
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => handleMessageInputChange(e)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  className="flex-1"
                />
                <Button onClick={sendMessage} disabled={!messageInput.trim()}>
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </>
        ) : pendingJoinRoom ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <h3 className="text-lg font-bold mb-2">Join {pendingJoinRoom.name}?</h3>
              <p className="text-gray-600 mb-4">{pendingJoinRoom.description || 'Join this room to start chatting'}</p>
              <Button onClick={() => handleJoinRoom(pendingJoinRoom)}>Join Room</Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-700">Select a room to start chatting</h3>
              <p className="text-gray-500 mt-2">Choose from the rooms on the left sidebar</p>
            </div>
          </div>
        )}
      </div>

      {/* Members Modal */}
      <MembersModal
        open={showMembersModal}
        onOpenChange={setShowMembersModal}
        members={members}
        searchQuery={memberSearchQuery}
        onSearchChange={setMemberSearchQuery}
        user={user}
        onViewProfile={viewProfile}
        onAddFriend={sendFriendRequest}
        onMuteUser={(member) => muteUser(member.id || member.user_id, selectedRoom?.id)}
        onKickUser={(member) => kickUser(member.id || member.user_id, selectedRoom?.id)}
        onBanUser={(member) => banUser(member.id || member.user_id, selectedRoom?.id)}
        onBlockUser={blockUser}
        onReportUser={(member) => openReportModal(member.id || member.user_id, member.username || member.user?.username)}
      />

      {/* Report User Modal */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-orange-500" />
              Report User
            </DialogTitle>
            <DialogDescription>
              Report {reportTarget?.username} for violating community guidelines
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="report-reason">Reason for Report</Label>
              <select
                id="report-reason"
                className="w-full mt-1 p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                <option value="spam">Spam or Advertising</option>
                <option value="harassment">Harassment or Bullying</option>
                <option value="hate_speech">Hate Speech</option>
                <option value="inappropriate">Inappropriate Content</option>
                <option value="impersonation">Impersonation</option>
                <option value="scam">Scam or Fraud</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setReportModalOpen(false);
                  setReportTarget(null);
                  setReportReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReportUser}
                disabled={!reportReason || reportSubmitting}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {reportSubmitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DesktopChatLayout;

